// ─────────────────────────────────────────────────────────────────────────
// Orchestrateur des suggestions exposant (« Pour vous »).
//   Pipeline conforme à la note experte :
//     1. isEligible()  → écarte les paires non éligibles (hard constraints)
//     2. getMatchScore() → pertinence 0..100 sur les paires restantes
//     3. seuil ≥ 50 → on n'affiche que les suggestions réellement pertinentes
//     4. tri décroissant, top N
//
//   La complétion de profil et la réputation n'interviennent NULLE PART ici
//   (elles relèvent de la Couche 3 / du gate, pas de la découvrabilité).
// ─────────────────────────────────────────────────────────────────────────

import { isEligible } from './eligibility';
import { getMatchScore, MatchBreakdown, MATCH_DISPLAY_THRESHOLD } from './relevance';

export { isEligible, ELIGIBILITY_LABELS } from './eligibility';
export type { EligibilityReason, EligibilityResult } from './eligibility';
export { getMatchScore, MATCH_DISPLAY_THRESHOLD } from './relevance';
export type { MatchScore, MatchBreakdown } from './relevance';

export interface StandProfile {
  standType?: string;
  region?: string;
  musicStyles?: string[];
  experienceRange?: string;
  maxDailyVolume?: string;
  [key: string]: unknown;
}

export interface FestivalSuggestion<F> {
  festival: F;
  score: number;
  breakdown: MatchBreakdown;
}

interface MinimalFestival {
  id: string;
  endDate?: string;
  location?: string;
  region?: string;
  musicStyles?: string[];
  standDetails?: Record<string, unknown>;
  totalStands?: number;
  capacity?: number;
}

/**
 * Calcule les suggestions « Pour vous » d'un exposant.
 * @param favorites IDs de festivals favoris (critère d'affinité).
 * @param opts.limit nombre max de suggestions (défaut 6).
 * @param opts.now horodatage injecté (déterminisme des tests).
 */
export function computeSuggestions<F extends MinimalFestival>(
  festivals: F[],
  stand: StandProfile,
  favorites: string[] = [],
  opts: { limit?: number; now?: number } = {}
): FestivalSuggestion<F>[] {
  const now = opts.now ?? Date.now();
  const limit = opts.limit ?? 6;

  return festivals
    .filter(f => isEligible(stand, f, now).eligible) // Couche 1
    .map(f => {
      const { score, breakdown } = getMatchScore(stand, f, favorites); // Couche 2
      return { festival: f, score, breakdown };
    })
    .filter(m => m.score >= MATCH_DISPLAY_THRESHOLD) // seuil ≥ 50
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export interface StandSuggestion<S> {
  stand: S;
  score: number;
  breakdown: MatchBreakdown;
}

interface MinimalStand extends StandProfile {
  id: string;
}

/**
 * Calcule les suggestions « Stands pour vous » d'un organisateur, pour UN
 * festival donné. Même pipeline et mêmes pondérations que computeSuggestions
 * (isEligible + getMatchScore) — simple inversion du sens de lecture de la
 * paire (stand, festival), aucune logique de scoring dupliquée.
 *
 * @param opts.limit nombre max de suggestions (défaut 12 — liste, pas widget).
 * @param opts.now horodatage injecté (déterminisme des tests).
 */
export function computeStandSuggestions<S extends MinimalStand>(
  stands: S[],
  festival: MinimalFestival,
  opts: { limit?: number; now?: number } = {}
): StandSuggestion<S>[] {
  const now = opts.now ?? Date.now();
  const limit = opts.limit ?? 12;

  return stands
    .filter(s => isEligible(s, festival, now).eligible) // Couche 1
    .map(s => {
      const { score, breakdown } = getMatchScore(s, festival); // Couche 2 (pas d'affinité orga→stand pour l'instant)
      return { stand: s, score, breakdown };
    })
    .filter(m => m.score >= MATCH_DISPLAY_THRESHOLD) // seuil ≥ 50, cohérent avec le côté exposant
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}
